import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { SERVICE_SECTION_KEYS, findSection } from "../services/contentApi";

const ServicePageContext = createContext(null);

export function ServicePageProvider({ fetchPage, children }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setPage(null);
    setError(null);

    fetchPage()
      .then((data) => {
        if (!cancelled) {
          setPage(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setPage(null);
        }
        console.error("Error fetching service page:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fetchPage]);

  const sections = useMemo(() => {
    const result = {};
    SERVICE_SECTION_KEYS.forEach((key) => {
      result[key] = findSection(page, key);
    });
    return result;
  }, [page]);

  const value = useMemo(
    () => ({ page, sections, loading, error }),
    [page, sections, loading, error]
  );

  return (
    <ServicePageContext.Provider value={value}>
      {children}
    </ServicePageContext.Provider>
  );
}

export function useServicePage() {
  const ctx = useContext(ServicePageContext);
  if (!ctx) {
    throw new Error("useServicePage must be used within a service page provider");
  }
  return ctx;
}

export function useServiceSection(key) {
  const ctx = useContext(ServicePageContext);
  if (!ctx) {
    return { section: null, loading: false, fromServicePage: false };
  }
  return {
    section: ctx.sections[key] ?? null,
    loading: ctx.loading,
    fromServicePage: true,
  };
}
